import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { CopiarTexto } from '@intelishs-group/ui';

/**
 * Las credenciales de SINAY de un empleado recien dado de alta.
 *
 * ── Por que existe esta pantalla ──────────────────────────────────────────
 *
 * Hasta el 2026-10-28 el alta pasaba por `rh.proc_alta_usuario`, que le ponia a
 * TODOS la misma contrasena —escrita en claro dentro del procedimiento— y
 * derivaba el usuario de un campo que capturaba RH. O sea: quien daba de alta ya
 * sabia las dos cosas y no habia nada que mostrar.
 *
 * Ahora las decide identidad: el usuario lo deriva de la persona y la contrasena
 * se genera por alta. **Son el unico momento en que existen** —de la base sale
 * el hash y nada mas— asi que si no se entregan aqui, el empleado no puede
 * entrar y nadie sabe por que.
 *
 * ── Es un PUENTE, y conviene recordarlo ──────────────────────────────────
 *
 * Lo dice el propio endpoint de identidad que las genera: cuando el servicio de
 * correo este conectado, esto se sustituye por un enlace de activacion y quien
 * da de alta no llega a ver la contrasena. Mientras tanto, mostrarla es la unica
 * forma de que el circuito cierre.
 *
 * ── Solo presenta ─────────────────────────────────────────────────────────
 *
 * No llama a nadie ni decide cuando cerrarse: recibe los dos valores y los
 * pinta. Quien lo abre decide si va en un modal o en la pantalla.
 */
@Component({
  selector: 'app-credenciales-nuevas',
  standalone: true,
  imports: [NzAlertModule, CopiarTexto],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p>
      <strong>{{ nombre() || 'El empleado' }}</strong> ya puede entrar a SINAY. Entrégale estas
      credenciales por un canal seguro: el sistema le pedirá cambiar la contraseña la primera vez
      que inicie sesión.
    </p>

    <dl class="credenciales">
      <div class="credenciales__fila">
        <dt class="credenciales__etiqueta">Usuario</dt>
        <dd class="credenciales__valor">
          <span class="sinay-mono">{{ usuario() }}</span>
          <intelishs-copiar-texto [texto]="usuario()" que="usuario" />
        </dd>
      </div>

      <div class="credenciales__fila">
        <dt class="credenciales__etiqueta">Contraseña</dt>
        <dd class="credenciales__valor">
          <span class="sinay-mono">{{ contrasena() }}</span>
          <intelishs-copiar-texto
            [texto]="contrasena()"
            que="contraseña"
            (copiado)="contrasenaCopiada.emit()"
          />
        </dd>
      </div>
    </dl>

    <nz-alert
      nzType="warning"
      nzShowIcon
      nzMessage="No se podrá volver a ver"
      [nzDescription]="avisoDetalle"
    ></nz-alert>

    <ng-template #avisoDetalle>
      El servidor guarda solo su cifrado y no la registra en ningún log. Si cierras sin copiarla,
      <strong>Recursos Humanos no puede reponerla</strong>: habrá que pedirlo a quien administra las
      cuentas de SINAY.
    </ng-template>
  `,
  styles: [
    `
      /* Solo COLOCACION: la apariencia de las piezas la pone el theme.
         Ver la seccion 1 de la skill sinay-frontend-ui. */
      :host {
        display: block;
      }

      /* El aviso cierra el bloque: va DESPUES de las credenciales, igual que en
         los dialogos de adminportal. Primero el dato que hay que llevarse, luego
         la advertencia de que no se repite — al reves, se lee la advertencia de
         algo que todavia no se ha visto. */
      nz-alert {
        margin-top: 16px;
      }

      p {
        margin: 0 0 16px;
      }

      .credenciales {
        display: grid;
        gap: 10px;
        margin: 0;
      }

      .credenciales__fila {
        display: grid;
        grid-template-columns: 120px minmax(0, 1fr);
        align-items: center;
        gap: 12px;
      }

      /* Por debajo de esto la etiqueta y el valor no caben en un renglon, y el
         valor es lo que NO se puede recortar. */
      @media (max-width: 480px) {
        .credenciales__fila {
          grid-template-columns: minmax(0, 1fr);
          gap: 2px;
        }
      }

      .credenciales__etiqueta {
        margin: 0;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        color: var(--sinay-text-secondary, #64748b);
      }

      .credenciales__valor {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        /* La contrasena puede ser larga y NO se recorta: es el dato, no una
           etiqueta. Envuelve antes que esconder un caracter. */
        min-width: 0;
      }

      .credenciales__valor .sinay-mono {
        font-size: 16px;
        word-break: break-all;
      }

    `,
  ],
})
export class CredencialesNuevasComponent {
  /** Con lo que la persona entra. Lo deriva identidad, no RH. */
  readonly usuario = input.required<string>();

  /** Existe solo en la respuesta del alta. */
  readonly contrasena = input.required<string>();

  /** Para la frase de arriba. Opcional: sin el, se dice «El empleado». */
  readonly nombre = input<string>('');

  /**
   * Se intentó copiar la CONTRASEÑA. De aquí cuelga el guardia que no deja
   * cerrar el modal.
   *
   * Emite aunque el portapapeles falle, igual que `(copiado)` de la librería: en
   * un contexto sin `navigator.clipboard` esperar el acierto dejaría a la
   * persona encerrada, y al fallar el componente ya dice «hazlo a mano» con el
   * valor en pantalla.
   */
  readonly contrasenaCopiada = output<void>();
}
